import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Package, MessageSquare, Box, ShoppingBag, Check, Activity, RefreshCw, AlertTriangle } from 'lucide-react';
import { adminFetch } from './shared';
import { AnalyticsDashboard } from './AnalyticsDashboard';

const ACTION_META = {
  order_created:        { icon: ShoppingBag,  color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Nuevo pedido' },
  order_status_changed: { icon: Package,      color: 'text-cyan-600',    bg: 'bg-cyan-50',    label: 'Status actualizado' },
  product_updated:      { icon: Box,          color: 'text-violet-600',  bg: 'bg-violet-50',  label: 'Producto editado' },
  contact_received:     { icon: MessageSquare,color: 'text-amber-600',   bg: 'bg-amber-50',   label: 'Mensaje recibido' },
  alert_sent:           { icon: AlertTriangle,color: 'text-rose-600',    bg: 'bg-rose-50',    label: 'Alerta' },
  watchdog_recovery:    { icon: Check,        color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Servicio recuperado' },
};

const formatRelativeTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const secs = Math.floor((Date.now() - d.getTime()) / 1000);
  if (secs < 60) return 'hace un instante';
  if (secs < 3600) return `hace ${Math.floor(secs / 60)} min`;
  if (secs < 86400) return `hace ${Math.floor(secs / 3600)} h`;
  return d.toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

// ==================== COUPONS TAB ====================

export const ActivityTab = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const res = await adminFetch('/api/admin/activity?limit=100');
      if (res.ok) {
        const d = await res.json();
        setEvents(d.events || []);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
    const interval = setInterval(fetchEvents, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = filter === 'all' ? events : events.filter(e => e.action === filter);
  const filterOptions = [
    { id: 'all', label: 'Todo' },
    { id: 'order_created', label: 'Pedidos' },
    { id: 'order_status_changed', label: 'Status' },
    { id: 'product_updated', label: 'Productos' },
    { id: 'contact_received', label: 'Mensajes' },
    { id: 'alert_sent', label: 'Alertas' },
  ];

  return (
    <div className="space-y-4" data-testid="activity-tab">
      <AnalyticsDashboard />
      {/* Filter pills */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {filterOptions.map(opt => (
          <button
            key={opt.id}
            onClick={() => setFilter(opt.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
              filter === opt.id
                ? 'bg-slate-900 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
            data-testid={`activity-filter-${opt.id}`}
          >
            {opt.label}
          </button>
        ))}
        <button
          onClick={fetchEvents}
          disabled={loading}
          className="ml-auto p-1.5 rounded-full hover:bg-slate-100 text-slate-500 disabled:opacity-50"
          title="Actualizar"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Event list */}
      {loading && events.length === 0 ? (
        <div className="text-center py-10 text-slate-500 text-sm">Cargando actividad...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-10 text-slate-400 text-sm">
          {filter === 'all'
            ? 'Aún no hay actividad registrada.'
            : 'Sin eventos en esta categoría.'}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(ev => {
            const meta = ACTION_META[ev.action] || { icon: Activity, color: 'text-slate-600', bg: 'bg-slate-50', label: ev.action };
            const Icon = meta.icon;
            return (
              <Card key={ev.id} className="border border-slate-200 hover:border-slate-300 transition-colors">
                <div className="p-3 flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-full ${meta.bg} flex items-center justify-center shrink-0`}>
                    <Icon className={`w-4 h-4 ${meta.color}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{meta.label}</p>
                      <span className="text-[10px] text-slate-400 shrink-0">{formatRelativeTime(ev.created_at)}</span>
                    </div>
                    <p className="text-sm text-slate-800 mt-0.5 break-words">{ev.summary}</p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <div className="text-center text-[11px] text-slate-400 pt-2">
        Auto-refresh cada 30s · Últimos {events.length} eventos (máx. 500 en buffer)
      </div>
    </div>
  );
};

// ==================== HEALTH TAB ====================
